/**
 * Fehlerbarriere: statt weißem Bildschirm eine Meldung.
 *
 * Anlass: „Bankdaten aus Überweisungsdatei“ gab beim Hochladen einen weißen
 * Bildschirm. Ursache war eine Fehlermeldung, die React ein Objekt als Kind
 * vorsetzte — das beendet den Renderbaum. Ohne Barriere räumt React dabei die
 * *gesamte* Oberfläche ab, und zu sehen ist nichts: gerade der Code, der den
 * Fehler melden sollte, war der Fehler.
 *
 * Bewusst ohne Button-, Dialog- oder Icon-Komponenten. Eine Barriere, die
 * selbst Abhängigkeiten mitbringt, kann beim Anzeigen erneut scheitern — dann
 * ist der Bildschirm wieder weiß.
 */
import React from 'react';

/** Fehlertext, der garantiert eine Zeichenkette ist. */
function lesbar(fehler) {
  if (!fehler) return '';
  if (typeof fehler === 'string') return fehler;
  if (typeof fehler.message === 'string' && fehler.message) return fehler.message;
  try {
    return JSON.stringify(fehler);
  } catch {
    return String(fehler);
  }
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { fehler: null };
  }

  static getDerivedStateFromError(fehler) {
    return { fehler };
  }

  componentDidCatch(fehler, info) {
    // Konsole, damit der Fehler beim Nachsehen auffindbar bleibt.
    // eslint-disable-next-line no-console
    console.error('Unbehandelter Fehler in der Oberfläche:', fehler, info?.componentStack);
  }

  render() {
    const { fehler } = this.state;
    if (!fehler) return this.props.children;

    return (
      <div
        className="min-h-[60vh] flex items-center justify-center p-6"
        data-testid="error-boundary"
      >
        <div className="max-w-lg w-full bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <h1 className="text-lg font-semibold text-slate-900">
            Diese Ansicht konnte nicht angezeigt werden
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Die Eingaben sind nicht verloren gegangen — gespeichert wurde aber auch nichts.
            Bitte die Seite neu laden. Tritt es an derselben Stelle wieder auf, hilft die
            folgende Meldung beim Nachsehen.
          </p>
          <p
            className="mt-3 p-2 rounded bg-slate-50 border border-slate-200 font-mono text-xs text-slate-700 break-words"
            data-testid="error-boundary-message"
          >
            {lesbar(fehler) || 'Kein Fehlertext vorhanden'}
          </p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-3 py-1.5 text-sm rounded-md bg-brand-red text-white hover:opacity-90"
              data-testid="error-boundary-reload"
            >
              Seite neu laden
            </button>
            <button
              type="button"
              onClick={() => this.setState({ fehler: null })}
              className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="error-boundary-retry"
            >
              Erneut versuchen
            </button>
          </div>
        </div>
      </div>
    );
  }
}
